import React from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Button from "./Button";
import type { PaginationData } from "../../types/exchange-rate";
import "./Pagination.css";

interface CommonProps {
  onPageChange: (page: number) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
}
type PaginationProps = CommonProps & ({
  currentPage: number;
  totalPages?: number;
  totalItems?: number;
  hasNext?: boolean;
  pagination?: never;
} | {
  pagination: PaginationData;
  currentPage?: never;
  totalPages?: never;
  totalItems?: never;
  hasNext?: never;
});

/** All lists share this control; pages own their data and current-page state. */
export default function Pagination(props: PaginationProps) {
  const rawPage = props.pagination?.current_page ?? props.currentPage ?? 1;
  const rawPages = props.pagination?.total_pages ?? props.totalPages;
  const total = props.pagination?.total ?? props.totalItems;
  const pages = rawPages === undefined ? undefined : Math.max(1, Math.floor(rawPages) || 1);
  const page = total === 0 ? 1 : Math.min(Math.max(1, Math.floor(rawPage) || 1), pages ?? Infinity);
  const busy = Boolean(props.disabled || props.loading);
  const nextAllowed = total !== 0 && (props.hasNext ?? (pages !== undefined && page < pages));
  const change = (next: number) => {
    if (!busy && total !== 0 && next >= 1 && (pages === undefined || next <= pages) && next !== page) props.onPageChange(next);
  };
  const visiblePages = pages === undefined ? [page] : Array.from(new Set([1, page - 1, page, page + 1, pages]))
    .filter(value => value >= 1 && value <= pages).sort((a, b) => a - b);

  return <nav className="navo-pagination" aria-label={props.label || "Pagination"} aria-busy={props.loading || undefined}>
    <p className="navo-pagination__summary" aria-live="polite">
      {props.loading ? "Loading results…" : <>
        {total !== undefined && <span>{total.toLocaleString()} {total === 1 ? "result" : "results"}<span className="navo-pagination__separator">·</span></span>}
        Page <strong>{page}</strong>{pages !== undefined && <> of <strong>{total === 0 ? 1 : pages}</strong></>}
      </>}
    </p>
    <div className="navo-pagination__controls">
      <Button className="navo-pagination__direction" aria-label="Previous page" disabled={busy || page <= 1 || total === 0} onClick={() => change(page - 1)}>
        <FiChevronLeft aria-hidden="true" /><span>Previous</span>
      </Button>
      {visiblePages.map((value, index) => <React.Fragment key={value}>
        {index > 0 && value - visiblePages[index - 1] > 1 && <span className="navo-pagination__ellipsis" aria-hidden="true">…</span>}
        <Button className={`navo-pagination__number ${value === page ? "is-current" : ""}`} aria-label={value === page ? `Current page, ${value}` : `Go to page ${value}`}
          aria-current={value === page ? "page" : undefined} disabled={busy || total === 0} onClick={() => change(value)}>{value}</Button>
      </React.Fragment>)}
      <Button className="navo-pagination__direction" aria-label="Next page" disabled={busy || !nextAllowed} onClick={() => change(page + 1)}>
        <span>Next</span><FiChevronRight aria-hidden="true" />
      </Button>
    </div>
  </nav>;
}
